/**
 * EmergencyContactRow — one row of the Emergency Contacts section.
 * Switches between read view and inline-edit form by the row's
 * `isEdit` flag (parent controls).
 *
 * Flutter parity:
 *   - `emergency_contacts_widget.dart` (read view + row actions)
 *   - `edit_emergency_contact_widget.dart` (inline edit form)
 *   - `create_emeregency_contact_widget.dart` [sic — Flutter typo]
 *
 * Read view: avatar (initials) · name · relation · phone · isProxy
 * badge · Edit/Remove buttons.
 *
 * Edit view: First/Middle/Last + Relation typeahead + Phone +
 * "Primary emergency contact" toggle + Done/Remove/Cancel buttons.
 */

import { Edit2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  EmergencyContactEditFields,
  isEmergencyContactSaveEnabled,
} from './emergency-contact-edit-fields';
import type { EmergencyContactRow as Row } from './emergency-contact-schema';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';

export interface EmergencyContactRowProps {
  row: Row;
  /** True when this is the only non-deleted row (Remove disabled). */
  isOnly: boolean;
  readOnly?: boolean;
  onChange: (next: Row) => void;
  onCancel: () => void;
  onRemove: () => void;
}

export function EmergencyContactRowComponent({
  row,
  isOnly,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: EmergencyContactRowProps) {
  if (row.isDeleted) return null;
  return row.isEdit ? (
    <EditCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onChange={onChange}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  ) : (
    <ReadCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onEdit={() => onChange({ ...row, isEdit: true })}
      onRemove={onRemove}
    />
  );
}

function ReadCard({
  row,
  isOnly,
  readOnly,
  onEdit,
  onRemove,
}: {
  row: Row;
  isOnly: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const fullName = [row.firstName, row.middleName, row.lastName]
    .filter((p) => p && p.trim())
    .join(' ') || 'Unnamed contact';

  return (
    <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-text-primary">{fullName}</p>
            {row.isProxy ? (
              <span className="[font:var(--type-label)] text-[var(--accent-active-text)] bg-[var(--accent-active-bg)] px-2 py-0.5 rounded-md">
                Primary
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            {row.relation.name ?? row.relationship ?? '—'} ·{' '}
            <span className="text-text-muted">{row.contactNumber || '—'}</span>
          </p>
        </div>
        {!readOnly ? (
          <div className="flex shrink-0 gap-1">
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="Edit emergency contact"
                    onClick={onEdit}
                    className={`${ROW_ICON_BUTTON} text-text-secondary`}
                  />
                }
              >
                <Edit2 className="size-4" aria-hidden />
              </TooltipTrigger>
              <TooltipContent>Edit emergency contact</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    aria-label="Remove emergency contact"
                    onClick={onRemove}
                    disabled={isOnly}
                    className={`${ROW_ICON_BUTTON} text-[var(--status-bad)]`}
                  />
                }
              >
                <Trash2 className="size-4" aria-hidden />
              </TooltipTrigger>
              <TooltipContent>Remove emergency contact</TooltipContent>
            </Tooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function EditCard({
  row,
  isOnly,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: {
  row: Row;
  isOnly: boolean;
  readOnly?: boolean;
  onChange: (next: Row) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-card border border-[var(--accent-primary)] bg-[var(--surface-new)] p-4 space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[var(--accent-active-text)]">
          {row.emergencyContactId ? 'Edit emergency contact' : 'New emergency contact'}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 rounded-md text-text-secondary hover:bg-grey-100"
          aria-label="Cancel edit"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <EmergencyContactEditFields row={row} readOnly={readOnly} onChange={onChange} />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--surface-line)]">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRemove}
          disabled={isOnly || readOnly}
        >
          Remove
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => onChange({ ...row, isEdit: false })}
          disabled={readOnly || !isEmergencyContactSaveEnabled(row)}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
